import { BASIS_POINTS_SCALE } from '../constants/index.ts';
import type { AppointmentPaymentStatus, PaymentStatus, PaymentType, QuotePaymentStatus } from '../constants/index.ts';
import { roundHalfUp } from './pricing.ts';
import { roundToCurrencyStep } from '../currency.ts';

/**
 * The payment-status rollup: an appointment's badge is derived from its ledger
 * rows, never stored independently of them. Pure so the Worker computes it on
 * every ledger change and tests can enumerate the edge cases cheaply.
 */

export interface LedgerEntry {
  type: PaymentType;
  status: PaymentStatus;
  amountCents: number;
  /**
   * Part of `amountCents`. A tip is thanks, not payment toward the work, so
   * it never pays a bill down — absent reads as zero, for callers (and rows)
   * that predate tips.
   */
  tipCents?: number;
  /**
   * Part of `amountCents`. The card fee is charged on top of the base, so
   * like the tip it never pays the bill down. Absent reads as zero.
   */
  cardFeeCents?: number;
  /**
   * The row's own id, and — on refunds — the charge the money went back on.
   * Optional for callers that predate tips: without the linkage a refund
   * nets against the whole ledger, which is exact whenever no tip is in play.
   */
  id?: string;
  refundOfPaymentId?: string | null;
  /**
   * When this row's money actually MOVED — `payments.settled_at`, falling back
   * to when the row was written for callers that have no better evidence.
   *
   * Only `peakSettledCents` reads it, and only to order its walk: a charge
   * inserted `pending` settles when the provider says so, which can be after
   * other activity on the same bill, so creation order is not the order the
   * balance actually took. Absent on every entry (as it is in the arithmetic
   * tests) leaves the caller's own order alone.
   */
  settledAt?: string | null;
}

/**
 * What a ledger of SUCCEEDED rows has actually SETTLED, in the one arithmetic
 * every surface that bills has to agree on.
 *
 * A charge credits its PRINCIPAL — its amount less the tip and the card fee —
 * because a tip is thanks and a fee rides on top, and neither pays the work
 * down. A refund is then capped at its OWN charge's principal: the refund
 * gives the tip back too, and letting that gross figure eat another charge's
 * credit would leave a document owed more than it is priced at (or owed again
 * after a second payment). Whatever a refund returns beyond the cap is the tip
 * going home. A refund naming no charge, or one outside this ledger, nets
 * against the whole — exact whenever no tip is in play.
 *
 * Stated once because three places compute it: `invoiceTotals` below for what
 * a document is owed, `manualInvoicePaymentStatus` for the status it is
 * stored with, and `OPEN_MANUAL_INVOICES_WHERE` in SQL for whether a customer
 * may be deleted. Callers filter to succeeded rows (and to one currency)
 * first; this counts what it is given.
 */
function settledPrincipalCents(entries: readonly LedgerEntry[]): number {
  const creditByCharge = new Map<string, number>();
  let unattributedCredit = 0;
  const refundsByCharge = new Map<string, number>();
  let unlinkedRefunds = 0;

  for (const entry of entries) {
    if (entry.type === 'charge') {
      const credit = entry.amountCents - (entry.tipCents ?? 0) - (entry.cardFeeCents ?? 0);
      if (entry.id) creditByCharge.set(entry.id, (creditByCharge.get(entry.id) ?? 0) + credit);
      else unattributedCredit += credit;
    } else if (entry.refundOfPaymentId) {
      refundsByCharge.set(
        entry.refundOfPaymentId,
        (refundsByCharge.get(entry.refundOfPaymentId) ?? 0) + entry.amountCents,
      );
    } else {
      unlinkedRefunds += entry.amountCents;
    }
  }

  let settled = unattributedCredit;
  for (const [chargeId, credit] of creditByCharge) {
    settled += Math.max(0, credit - (refundsByCharge.get(chargeId) ?? 0));
  }
  for (const [chargeId, amount] of refundsByCharge) {
    if (!creditByCharge.has(chargeId)) unlinkedRefunds += amount;
  }
  return Math.max(0, settled - unlinkedRefunds);
}

/**
 * The most this ledger ever settled AT ONE TIME — the question "was this bill
 * ever actually covered?".
 *
 * Lifetime charge volume answers a different question and answers it wrongly:
 * a $100 bill paid $60, refunded, then paid $40 and refunded again has taken
 * $100 of charges and was never once covered. Only a running balance can tell
 * those apart, and the order that balance has to be walked in is the order the
 * money MOVED — `settledAt` — not the order the rows were written.
 *
 * Those are different orders, and the difference is not exotic. An online
 * charge is written `pending` and settles when the provider says so, which can
 * be after a whole cash payment has been taken and handed back on the same
 * bill. A $60 charge left pending, $40 taken and refunded, then the $60
 * settled and refunded, is a bill whose balance never passed $60 — but read in
 * creation order both charges land before the first refund and it reads as
 * having reached $100, which retires a fully owed bill behind a terminal
 * 'refunded'. So the walk sorts by settlement first, stably, leaving the
 * caller's own order to break ties and to stand in whole where no entry
 * carries a settlement instant (the arithmetic tests, and any caller that
 * predates the column).
 *
 * Rows that arrive out of order cannot make this overstate coverage: a refund
 * is recognised by the charge it names whenever that charge is anywhere in
 * the ledger, and is capped at what that charge still has settling — the same
 * cap `settledPrincipalCents` applies, so the walk ends on the figure that
 * function returns.
 */
function peakSettledCents(entries: readonly LedgerEntry[]): number {
  // Every charge in the ledger, before the walk: a refund naming one of them
  // is its charge's refund wherever the row happens to sit.
  const remainingByCharge = new Map<string, number>();
  for (const entry of entries) {
    if (entry.type === 'charge' && entry.id) remainingByCharge.set(entry.id, 0);
  }

  // Settlement chronology. `sort` is stable in every engine this runs on, and
  // an absent `settledAt` reads as the same empty key on every entry, so a
  // ledger that carries none comes back in exactly the order it was given.
  // Compared as plain strings rather than parsed: these are ISO-8601 UTC
  // instants, whose lexical order IS their chronological order.
  const settledKey = (entry: LedgerEntry) => entry.settledAt ?? '';
  const walk = [...entries].sort((a, b) => {
    const left = settledKey(a);
    const right = settledKey(b);
    return left < right ? -1 : left > right ? 1 : 0;
  });

  let running = 0;
  let peak = 0;
  for (const entry of walk) {
    if (entry.type === 'charge') {
      const credit = entry.amountCents - (entry.tipCents ?? 0) - (entry.cardFeeCents ?? 0);
      if (entry.id) remainingByCharge.set(entry.id, (remainingByCharge.get(entry.id) ?? 0) + credit);
      running += credit;
      if (running > peak) peak = running;
    } else {
      const chargeId = entry.refundOfPaymentId;
      const remaining = chargeId === null || chargeId === undefined ? undefined : remainingByCharge.get(chargeId);
      if (remaining !== undefined && chargeId !== null && chargeId !== undefined) {
        const applied = Math.min(entry.amountCents, Math.max(0, remaining));
        remainingByCharge.set(chargeId, remaining - applied);
        running -= applied;
      } else {
        // Naming no charge, or one outside this ledger: nets against the whole.
        running -= entry.amountCents;
      }
    }
  }
  return peak;
}

export function appointmentPaymentStatus(entries: readonly LedgerEntry[]): AppointmentPaymentStatus {
  let charged = 0;
  let refunded = 0;
  let hasPendingCharge = false;
  let hasFailedCharge = false;

  for (const entry of entries) {
    if (entry.type === 'charge') {
      if (entry.status === 'succeeded') charged += entry.amountCents;
      else if (entry.status === 'pending') hasPendingCharge = true;
      else if (entry.status === 'failed') hasFailedCharge = true;
    } else if (entry.type === 'refund' && entry.status === 'succeeded') {
      refunded += entry.amountCents;
    }
  }

  // Money collected wins over anything still in flight: a paid visit with a
  // second pending adjustment is still, above all, paid.
  if (charged > 0) {
    if (refunded >= charged) return 'refunded';
    if (refunded > 0) return 'partially_refunded';
    return 'paid';
  }
  if (hasPendingCharge) return 'pending';
  if (hasFailedCharge) return 'failed';
  return 'not_billed';
}

/**
 * The rollup for a MANUAL invoice — one the office wrote by hand out of line
 * items (migration 0103), whose ledger is the rows carrying its
 * `invoice_id` rather than any visit's.
 *
 * A positive total is a sale, and — like the credit memo below, and unlike a
 * visit — it is TOTAL-AWARE. It cannot delegate to `appointmentPaymentStatus`:
 * that one says 'paid' the moment any succeeded charge lands, which holds for
 * a visit (whose charge always covers the whole price) and is false here,
 * because a manual invoice deliberately accepts part-payments
 * (`insertInvoiceChargeIfPayable`). A $50 payment on a $200 invoice stored as
 * 'paid' hides $150 the office still has to collect: the badge reads Paid, the
 * unpaid filters drop the document, and the list's own actions go with it.
 *
 * So the sale side asks what the ledger has actually SETTLED — succeeded
 * charges net of their tip and card fee (neither pays the bill down; the same
 * principal `invoiceTotals` and the charge guard count), with each refund
 * capped at its own charge's credit — and says:
 *
 *   - 'paid' once that covers the total;
 *   - for anything less, whether the bill was EVER covered decides the
 *     vocabulary. A bill that reached its total and then had money go back is
 *     'partially_refunded' (some still settling it) or 'refunded' (all of it
 *     back). A bill that never reached its total is not refunded, whatever
 *     has moved across it — it is simply still owed, and gets the open status
 *     its charges call for: 'pending' or 'failed' while one is in flight or
 *     has just failed, else 'not_billed'. Those are the statuses the unpaid
 *     filters and the list's own row actions read as "still owed", so the
 *     bill stays in the workflow that collects it.
 *
 * "Ever covered" is a running balance, not lifetime charge volume: $60 paid
 * and refunded, then $40 paid and refunded, is $100 of charges on a $100 bill
 * that was never once covered and is still owed in full. `peakSettledCents`
 * answers it by walking the ledger in the order the money SETTLED, which is
 * not the order the rows were written whenever a charge sat pending while the
 * bill went on moving.
 *
 * A negative total is a credit memo, and the mirror image: it is settled by
 * REFUNDS — a card payment refunded against it, or cash handed back — and its
 * ledger is read with the roles swapped, 'refunded' only once the whole
 * credit has gone back and 'partially_refunded' before that.
 */
export function manualInvoicePaymentStatus(
  totalCents: number,
  entries: readonly LedgerEntry[],
): AppointmentPaymentStatus {
  if (totalCents > 0) {
    let givenBack = 0;
    let hasPendingCharge = false;
    let hasFailedCharge = false;
    for (const entry of entries) {
      if (entry.type === 'charge') {
        if (entry.status === 'pending') hasPendingCharge = true;
        else if (entry.status === 'failed') hasFailedCharge = true;
      } else if (entry.type === 'refund' && entry.status === 'succeeded') {
        givenBack += entry.amountCents;
      }
    }

    const succeeded = entries.filter((entry) => entry.status === 'succeeded');
    const collected = settledPrincipalCents(succeeded);
    if (collected >= totalCents) return 'paid';
    // The refund vocabulary belongs to a bill that WAS covered: 'refunded'
    // and 'partially_refunded' both say "this was settled and money came
    // back", and both are read as terminal — out of the unpaid filters, and
    // out of the row actions that collect the rest. A bill that never reached
    // its total has nothing to be refunded FROM, whatever has moved across
    // it: $50 taken and $10 handed back on a $200 invoice is $190 still to
    // collect, and it stays in the workflow that collects it.
    if (givenBack > 0 && peakSettledCents(succeeded) >= totalCents) {
      return collected > 0 ? 'partially_refunded' : 'refunded';
    }
    if (hasPendingCharge) return 'pending';
    if (hasFailedCharge) return 'failed';
    return 'not_billed';
  }

  let refunded = 0;
  let charged = 0;
  let hasPendingRefund = false;
  let hasFailedRefund = false;
  for (const entry of entries) {
    if (entry.type === 'refund') {
      if (entry.status === 'succeeded') refunded += entry.amountCents;
      else if (entry.status === 'pending') hasPendingRefund = true;
      else if (entry.status === 'failed') hasFailedRefund = true;
    } else if (entry.type === 'charge' && entry.status === 'succeeded') {
      charged += entry.amountCents;
    }
  }

  const givenBack = refunded - charged;
  if (givenBack > 0) {
    if (givenBack >= -totalCents) return 'refunded';
    return 'partially_refunded';
  }
  if (hasPendingRefund) return 'pending';
  if (hasFailedRefund) return 'failed';
  return 'not_billed';
}

/** Where an accepted one-time quote's money stands: see `quotePaymentSummary`. */
export interface QuotePaymentSummary {
  status: QuotePaymentStatus;
  /** What the payments on the quote have settled: principal, net of refunds. */
  paidCents: number;
  /** The quote's total less `paidCents`, never below zero. */
  balanceCents: number;
}

/**
 * Paid, Partly paid or Unpaid for a quote, from the payments recorded on the
 * quote itself.
 *
 * A quote is a bill the customer may pay in parts — a deposit at the
 * walkthrough, the rest on the day — which is exactly the case
 * `manualInvoicePaymentStatus` was written for, so "paid" is its answer and
 * not a second opinion: the settled principal (tips and card fees never pay
 * the work down, each refund capped at its own charge) has reached the total.
 * The visit rollup would be wrong here, since it says paid at the first
 * charge of any size.
 *
 * Below the total the invoice vocabulary has more words than a quote needs —
 * pending and failed belong to card payments, which a quote does not take —
 * so it folds to the two the office reads: Partly paid while some principal
 * is settled, Unpaid while none is. A quote that was paid and then refunded
 * in full is Unpaid, because nothing is paid on it.
 *
 * Callers decide whether a quote has a status at all: only an accepted
 * one-time quote with something to pay does (`quoteTracksPayment`).
 */
export function quotePaymentSummary(totalCents: number, entries: readonly LedgerEntry[]): QuotePaymentSummary {
  const paidCents = quotePaidCents(entries);
  const balanceCents = Math.max(0, totalCents - paidCents);
  if (manualInvoicePaymentStatus(totalCents, entries) === 'paid') {
    return { status: 'paid', paidCents, balanceCents };
  }
  return { status: paidCents > 0 ? 'partly_paid' : 'unpaid', paidCents, balanceCents };
}

/**
 * What the payments on a quote have settled: `quotePaymentSummary`'s
 * `paidCents`, for a quote with no total to measure it against (a recurring
 * quote's deposit is still money already taken). Each refund is capped at its
 * own charge, so a refunded tip never eats another payment's credit.
 */
export function quotePaidCents(entries: readonly LedgerEntry[]): number {
  return settledPrincipalCents(entries.filter((entry) => entry.status === 'succeeded'));
}

/**
 * How much of a credit memo has not yet been given back, from succeeded rows
 * alone — refunds still in flight show as the invoice's `pending` status, not
 * as money already returned. Zero for anything that is not a credit memo.
 *
 * This is the figure that DESCRIBES the memo: what the invoice screen, the PDF
 * and the customer's own copy say is still owed back. What may be sent right
 * now is `creditRefundableNowCents` below, which is never larger.
 */
export function creditRemainingCents(totalCents: number, entries: readonly LedgerEntry[]): number {
  if (totalCents >= 0) return 0;
  let givenBack = 0;
  for (const entry of entries) {
    if (entry.status !== 'succeeded') continue;
    givenBack += entry.type === 'refund' ? entry.amountCents : -entry.amountCents;
  }
  return Math.max(0, -totalCents - givenBack);
}

/**
 * The same credit, as the amount that may actually be SENT right now: what is
 * still owed back, less the refunds already on their way.
 *
 * The two differ only while a refund is in flight, and only one of them is a
 * cap. `CREDIT_REMAINING_GUARD` — the arithmetic inside the insert, which is
 * the real authority — counts `pending` refunds, because a refund the provider
 * has taken but not confirmed may already be money out the door. A form
 * offering the descriptive figure would let an operator ask for an amount the
 * database then refuses, after they have told the customer it is on its way.
 *
 * Kept as its own function rather than folded into the one above, because the
 * memo's own total must not shrink for money that has not gone anywhere yet:
 * "still owed back" is a fact about the document, "can be sent now" is a fact
 * about this moment.
 */
export function creditRefundableNowCents(totalCents: number, entries: readonly LedgerEntry[]): number {
  if (totalCents >= 0) return 0;
  let givenBack = 0;
  for (const entry of entries) {
    if (entry.type === 'refund') {
      if (entry.status === 'succeeded' || entry.status === 'pending') givenBack += entry.amountCents;
    } else if (entry.status === 'succeeded') {
      givenBack -= entry.amountCents;
    }
  }
  return Math.max(0, -totalCents - givenBack);
}

/** How much of the succeeded charges is still refundable. */
export function refundableCents(entries: readonly LedgerEntry[]): number {
  let charged = 0;
  let refunded = 0;
  for (const entry of entries) {
    if (entry.status !== 'succeeded') continue;
    if (entry.type === 'charge') charged += entry.amountCents;
    else refunded += entry.amountCents;
  }
  return Math.max(0, charged - refunded);
}

/** A ledger row as the per-charge cap below needs to see it. */
export interface ChargeLedgerEntry extends LedgerEntry {
  id: string;
  /** Which charge a refund row gives money back on; null on charges. */
  refundOfPaymentId: string | null;
}

/**
 * What is still refundable on one specific charge.
 *
 * Narrower than `refundableCents` above, and deliberately so: the provider
 * refunds against a single payment, so a visit paid by two cards has two
 * independent caps rather than one pooled figure. This is the same arithmetic
 * the refund route applies (`refundedCentsForCharge`), stated once so the form
 * that offers an amount and the endpoint that accepts it cannot drift — a UI
 * that offers more than the server will take is a button that fails after the
 * operator has already told the customer the money is on its way.
 *
 * Which is why a refund still IN FLIGHT is counted here exactly as the server
 * counts it: a provider that has not confirmed a refund leaves the row
 * `pending` (`refundPayment`), and that money may already be gone. It is not
 * shown as returned anywhere — the rollups above count only what settled —
 * but it is not offered again either.
 */
export function refundableCentsForCharge(chargeId: string, entries: readonly ChargeLedgerEntry[]): number {
  let charged = 0;
  let refunded = 0;
  for (const entry of entries) {
    if (entry.type === 'charge' && entry.id === chargeId) {
      if (entry.status === 'succeeded') charged += entry.amountCents;
    } else if (entry.type === 'refund' && entry.refundOfPaymentId === chargeId) {
      if (entry.status === 'succeeded' || entry.status === 'pending') refunded += entry.amountCents;
    }
  }
  return Math.max(0, charged - refunded);
}

/**
 * Whether one charge's money has actually gone back — settled refunds only.
 *
 * The cap above counts refunds still in flight, and must: money that may
 * already be gone cannot be offered a second time. But a reservation is not a
 * refund, and words are not a cap. Reading "nothing left to refund" as "fully
 * refunded" puts those two sentences on one row — a refund the ledger shows as
 * unconfirmed, beside a line telling the operator the charge was refunded in
 * full — which is the ledger contradicting itself on the one screen where
 * somebody is deciding whether to send money again.
 *
 * So the cap answers what may be sent, and this answers what has been. They
 * differ exactly while a refund is unresolved, which is when the difference
 * matters.
 */
export function chargeFullyRefunded(chargeId: string, entries: readonly ChargeLedgerEntry[]): boolean {
  let charged = 0;
  let refunded = 0;
  for (const entry of entries) {
    if (entry.status !== 'succeeded') continue;
    if (entry.type === 'charge' && entry.id === chargeId) charged += entry.amountCents;
    else if (entry.type === 'refund' && entry.refundOfPaymentId === chargeId) refunded += entry.amountCents;
  }
  return charged > 0 && refunded >= charged;
}

/**
 * A ledger row as the invoice rollup below needs to see it: everything a
 * `LedgerEntry` carries, plus the currency it moved in.
 */
export interface InvoiceLedgerEntry extends LedgerEntry {
  currencyCode: string;
}

export interface InvoiceTotals {
  /** Succeeded charges net of their tips — card fee included, tips not: a tip never pays the bill. */
  paidCents: number;
  /** Succeeded refunds, gross — that is the money that actually went back. */
  refundedCents: number;
  /**
   * What currently settles the work: paid credit less refunds, with each
   * refund capped at its own charge's credit. This — not `paidCents -
   * refundedCents` — is the number to DISPLAY as "paid so far": the two
   * fields sit on different bases (paid is net of tips, refunded is gross),
   * so their difference understates the money on the bill whenever a tipped
   * charge was refunded and the invoice was paid again.
   */
  collectedCents: number;
  /** Still owed: the visit's price less `collectedCents`, never below zero. */
  balanceCents: number;
}

/**
 * What an invoice says it is owed, from the ledger of the visit it bills.
 *
 * Shared rather than computed in the route, because the same three numbers are
 * printed on the PDF and shown on the invoices screen, and an invoice whose
 * document and whose screen disagree about the balance is worse than either
 * being wrong on its own.
 *
 * The balance floors at zero. A card fee is charged on top of the price, so a
 * fully paid visit routinely settles for more than it was priced at — that is
 * a fee the customer agreed to, not a credit the company owes back.
 *
 * A tip is different from the fee in the one way that matters here: a fee
 * only ever rides on a charge that pays the balance in full, while a tip can
 * ride on a PARTIAL payment — $100 toward a $150 visit plus a $50 tip. Count
 * the whole row and that invoice reads paid-in-full with $50 still owed, so
 * charges are credited net of their tip. The tip still shows on the row
 * itself; it just does not pay the bill down.
 */
export function invoiceTotals(
  priceCents: number | null,
  currencyCode: string,
  entries: readonly InvoiceLedgerEntry[],
): InvoiceTotals {
  let paidCents = 0;
  let refundedCents = 0;
  // A visit priced in dollars and paid in euros cannot be summed: adding the
  // two would understate the balance by whatever the exchange rate happens to
  // be, and there is no rate here to convert with. Such a row still *appears*
  // on the invoice, in its own currency — it just does not move a total it
  // cannot be added to.
  const counted = entries.filter((entry) => entry.currencyCode === currencyCode && entry.status === 'succeeded');

  for (const entry of counted) {
    if (entry.type === 'charge') {
      // `paidCents` stays fee-inclusive — see its own doc — because that row
      // displays what was actually paid; what it SETTLED is the capped
      // principal below.
      paidCents += entry.amountCents - (entry.tipCents ?? 0);
    } else {
      // Gross, deliberately: this is the money that actually went back, and
      // the display owes the customer that number even where the balance
      // arithmetic caps what it can un-pay.
      refundedCents += entry.amountCents;
    }
  }

  const collected = settledPrincipalCents(counted);
  const balanceCents = priceCents === null ? 0 : Math.max(0, priceCents - collected);
  return { paidCents, refundedCents, collectedCents: collected, balanceCents };
}

// ---------------------------------------------------------------------------
// Card-processing fee
// ---------------------------------------------------------------------------

/**
 * The percentage a company may add when a customer pays online by card
 * (`company_payment_settings.card_fee_*`). Off unless a company switched it on,
 * and expressed in basis points so a decimal rate like 2.9% stays an integer
 * everywhere: 290.
 */
export interface CardFeePolicy {
  readonly enabled: boolean;
  readonly basisPoints: number;
}

/** A rate above this is a typo, not a surcharge. */
export const MAX_CARD_FEE_BASIS_POINTS = 2_000;

/**
 * The largest amount that may reach a provider, fee included.
 *
 * Bounds the total rather than the base: a base at the limit plus a fee is
 * over it, and providers reject an amount past their own digit limit — which
 * would have shown up as a declined card rather than as the mis-priced charge
 * it is.
 */
export const MAX_CHARGE_AMOUNT_CENTS = 100_000_000;

export const NO_CARD_FEE: CardFeePolicy = { enabled: false, basisPoints: 0 };

export interface CardFeeBreakdown {
  /** What the work costs, before the fee. */
  readonly baseCents: number;
  readonly feeCents: number;
  /** What the card is actually charged. */
  readonly totalCents: number;
  readonly basisPoints: number;
}

/** Whether this policy adds anything at all. A rate of zero is the same as off. */
export function cardFeeApplies(policy: CardFeePolicy | null | undefined): boolean {
  return Boolean(policy?.enabled && policy.basisPoints > 0);
}

/**
 * Whether the ADVERTISED fee moved — on/off, or the rate while on. The
 * question every settings transition asks before marking stored quote PDFs
 * stale, so it lives beside the policy it compares rather than in any one
 * route.
 */
export function cardFeePoliciesDiffer(before: CardFeePolicy, after: CardFeePolicy): boolean {
  return (
    cardFeeApplies(before) !== cardFeeApplies(after) ||
    (cardFeeApplies(after) && before.basisPoints !== after.basisPoints)
  );
}

/**
 * Splits an amount into what is being billed and the fee added on top.
 *
 * The fee is computed on the base, so the customer pays `base + base × rate` —
 * the company still nets slightly under its base amount once the provider
 * takes its own cut, which is the honest arithmetic to show and the only one
 * that is legal in the jurisdictions that cap surcharges at cost.
 *
 * The fee is then rounded to the currency's own step, not just to whole cents.
 * A percentage of a legal ¥15 is ¥0.435, and there is no such thing — the base
 * was chargeable and the total has to stay chargeable, so the part being added
 * is what gets rounded.
 */
export function applyCardFee(
  baseCents: number,
  policy: CardFeePolicy | null | undefined,
  currencyCode: string,
): CardFeeBreakdown {
  if (!cardFeeApplies(policy) || baseCents <= 0) {
    return { baseCents, feeCents: 0, totalCents: baseCents, basisPoints: 0 };
  }
  const basisPoints = policy!.basisPoints;
  const exact = roundHalfUp((baseCents * basisPoints) / BASIS_POINTS_SCALE);
  const feeCents = roundToCurrencyStep(exact, currencyCode);
  return { baseCents, feeCents, totalCents: baseCents + feeCents, basisPoints };
}

/**
 * The fee a stored card may actually be charged.
 *
 * A card on file is charged without the customer present, so the only consent
 * behind it is the one given when the card was saved — against whatever rate
 * was in force then. Raising the rate afterwards cannot reach back to a card
 * that agreed to less, and a card saved while the fee was switched off agreed
 * to none at all.
 *
 * Lowering the rate does reach back, since `min` never charges more than the
 * company currently asks of anyone.
 */
export function cardOnFilePolicy(current: CardFeePolicy, consentedBasisPoints: number): CardFeePolicy {
  if (!cardFeeApplies(current)) return NO_CARD_FEE;
  const basisPoints = Math.min(current.basisPoints, Math.max(0, consentedBasisPoints));
  return basisPoints > 0 ? { enabled: true, basisPoints } : NO_CARD_FEE;
}

/** "2.9%" from 290, trimmed so a whole percentage does not read "3.00%". */
export function formatBasisPointsPercent(basisPoints: number): string {
  return `${(basisPoints / 100).toFixed(2).replace(/\.?0+$/, '')}%`;
}

// ---------------------------------------------------------------------------
// Tips
// ---------------------------------------------------------------------------

/**
 * The preset percentages every tip surface offers, in basis points. Fixed
 * rather than per-company configuration on purpose: three familiar choices
 * plus "other amount" covers the act of tipping, and a per-company preset
 * editor would be settings surface for a number the customer types anyway.
 * Served to clients from `GET /payments/status` so a future change here (or a
 * server that decides to vary them) needs no client release.
 */
export const TIP_PRESET_BASIS_POINTS: readonly number[] = [1000, 1500, 2000];

/**
 * A preset tip for a base amount: the percentage, rounded to the currency's
 * own step the same way the card fee is — the base was chargeable, and what
 * is being added must keep the total chargeable.
 */
export function tipPresetCents(baseCents: number, basisPoints: number, currencyCode: string): number {
  if (baseCents <= 0 || basisPoints <= 0) return 0;
  return roundToCurrencyStep(roundHalfUp((baseCents * basisPoints) / BASIS_POINTS_SCALE), currencyCode);
}

/**
 * A tip as the ledger may carry it: whole, non-negative, and on the
 * currency's step. Rounded rather than refused — a customer adding "$5.005"
 * of generosity through a percentage preset should not be told no over a
 * fraction the currency cannot express.
 */
export function normalizeTipCents(tipCents: number | null | undefined, currencyCode: string): number {
  if (!tipCents || tipCents <= 0) return 0;
  return roundToCurrencyStep(tipCents, currencyCode);
}
